// [PURPOSE]: Pure helper that decrements consumed material components from
//   inventory when a spell with `componentDetails.consumed: true` is cast.
// [WHY]: Tier 1.15 — CustomSpellCreator advertised "Components are consumed
//   on cast" and persisted `componentDetails.consumed: true`, but
//   spellCastingService never honored it for the common case (no
//   specificComponents list, or zero-cost consumed components, or generic
//   description-only consumed components). The form was lying to the user.
//
//   This helper is the single source of truth for "did we consume?" — wired
//   into spellCastingService.castSpell after slot/charge/per-rest deduction
//   and before the SPELL_CAST event is emitted.
//
// [INTEGRATION]:
//   - Reads spell.componentDetails (the shape persisted by CustomSpellCreator)
//   - Mutates inventory via the supplied setInventory updater (functional form)
//   - Calls logActivity with type 'spell-component-consumed'
//   - Returns {consumed, skipped} so the caller can show a non-blocking toast
//     when a component was missing (player can self-track manually).
//
// [SCOPE]: SRD spell consumption (e.g., Revivify diamond, Resurrection
//   diamond) is NOT yet wired through this helper — SRD spells use the
//   legacy SPELL_COMPONENTS lookup in App.jsx consumeSpellComponents.
//   Migrating SRD data to use this helper is a v1.1 task.
//
// [ARC-014 / Rule 11]: Matching IS string-based here, but the strings are
//   user-supplied content (custom spell name → user-supplied component name)
//   not domain concept matching. The user-facing spell creator is the source
//   of truth; the helper does the best-effort name match into inventory.
//   This is acceptable because the alternative — requiring the player to
//   pick a structured itemId at spell-creation time — is a UX regression.

/**
 * Try to consume a single specific-component entry from inventory.
 * Returns the matched item id (so we can decrement) or null if not found.
 *
 * Match rules:
 *   - Item name (lower-cased) contains reqComponent.name (lower-cased) OR
 *     reqComponent.name (lower-cased) contains item name (lower-cased).
 *   - Item value >= reqComponent.cost (or cost is 0 / unspecified).
 */
function findInventoryMatchForRequired(reqComponent, inventory) {
  if (!reqComponent || !reqComponent.name) return null;
  const targetName = String(reqComponent.name).toLowerCase().trim();
  const targetCost = Number(reqComponent.cost) || 0;

  return inventory.find(item => {
    if (!item || !item.name) return false;
    const itemName = String(item.name).toLowerCase().trim();
    const nameMatch = itemName.includes(targetName) || targetName.includes(itemName);
    if (!nameMatch) return false;
    const valueMet = !targetCost || (Number(item.value) || 0) >= targetCost;
    return valueMet;
  }) || null;
}

/**
 * Try to find an inventory match for a generic free-text description.
 * Used when componentDetails has `consumed: true` but no specificComponents
 * list — we extract significant tokens from the description and look for any
 * inventory item that contains one of them in its name.
 *
 * Heuristic: split on whitespace, drop common filler ('a', 'an', 'the',
 * 'worth', 'at', 'least', 'gp', 'sp', etc. and any pure-numeric token), and
 * try the remaining tokens against item names in order. First match wins.
 */
const FILLER_TOKENS = new Set([
  'a', 'an', 'the', 'of', 'or', 'and', 'with', 'in', 'on', 'to',
  'worth', 'at', 'least', 'value', 'valued',
  'gp', 'sp', 'cp', 'pp', 'gold', 'silver', 'copper', 'platinum',
  'piece', 'pieces', 'each',
]);

function findInventoryMatchForDescription(description, inventory) {
  if (!description || typeof description !== 'string') return null;
  const tokens = description
    .toLowerCase()
    .replace(/[(),.]/g, ' ')
    .split(/\s+/)
    .filter(t => t && !FILLER_TOKENS.has(t) && !/^\d+$/.test(t));

  if (tokens.length === 0) return null;

  for (const token of tokens) {
    if (token.length < 3) continue; // skip 'an', 'or', stub tokens
    const match = inventory.find(item => {
      if (!item || !item.name) return false;
      return String(item.name).toLowerCase().includes(token);
    });
    if (match) return match;
  }
  return null;
}

/**
 * Apply a single inventory decrement for a matched item — either reduce
 * stack quantity by 1 or remove the item entirely.
 *
 * Returns the updater function to pass to setInventory.
 */
function buildInventoryDecrementUpdater(matchedItemId) {
  return (prev) => {
    const idx = prev.findIndex(i => i.id === matchedItemId);
    if (idx === -1) return prev;
    const item = prev[idx];
    if (item.stackable && (item.quantity || 1) > 1) {
      const next = [...prev];
      next[idx] = { ...item, quantity: item.quantity - 1 };
      return next;
    }
    return prev.filter(i => i.id !== matchedItemId);
  };
}

/**
 * Consume material components for a spell cast.
 *
 * @param {Object} spell - The spell object (from CustomSpellCreator or SRD)
 * @param {Array} inventory - Current inventory snapshot (read-only here)
 * @param {Function} setInventory - Functional updater (prev => next)
 * @param {Function} logActivity - (message, type, details) — Tabletop log
 * @returns {{consumed: Array, skipped: Array}}
 *   consumed: [{itemId, name, value}]
 *   skipped:  [{name, reason: 'not_in_inventory' | 'no_component_specified'}]
 *
 * Returns no-op (empty arrays) when:
 *   - spell has no componentDetails, OR
 *   - componentDetails.consumed is falsy.
 *
 * RAW note: arcane focus / component pouch substitution does NOT apply to
 * components flagged as consumed. The pre-flight check (canCastSpell) handles
 * the substitution for non-consumed components; this helper only runs for
 * the consumed case so substitution is irrelevant here.
 */
export function consumeMaterialComponents(spell, inventory, setInventory, logActivity) {
  const result = { consumed: [], skipped: [] };

  if (!spell || !spell.componentDetails) return result;
  const details = spell.componentDetails;
  if (!details.consumed) return result;

  const spellName = spell.name || 'Unknown Spell';
  const inv = Array.isArray(inventory) ? inventory : [];

  // Path 1: structured specificComponents list. Process each entry whose
  // own consumed flag is also true (the per-component override beats the
  // top-level flag — Pearl in Identify is reusable even though materials
  // are "consumed" overall in some spells).
  const list = Array.isArray(details.specificComponents) ? details.specificComponents : [];
  const consumedList = list.filter(c => c && c.consumed);

  if (consumedList.length > 0) {
    // Track inventory state across multiple decrements within this call so
    // we don't double-match the same item.
    let workingInv = inv.slice();

    for (const reqComponent of consumedList) {
      const matched = findInventoryMatchForRequired(reqComponent, workingInv);
      if (matched) {
        const updater = buildInventoryDecrementUpdater(matched.id);
        // Apply the decrement to setInventory AND mirror it in workingInv so
        // subsequent iterations don't see the now-consumed item.
        if (typeof setInventory === 'function') {
          setInventory(updater);
        }
        workingInv = updater(workingInv);

        result.consumed.push({
          itemId: matched.id,
          name: matched.name,
          value: Number(matched.value) || 0,
        });

        if (typeof logActivity === 'function') {
          logActivity(
            `Consumed ${matched.name} for ${spellName}`,
            'spell-component-consumed',
            {
              spell: spellName,
              component: matched.name,
              itemId: matched.id,
              value: Number(matched.value) || 0,
              consumed: true,
            }
          );
        }
      } else {
        result.skipped.push({
          name: reqComponent.name || 'unknown',
          reason: 'not_in_inventory',
        });
        if (typeof logActivity === 'function') {
          logActivity(
            `Could not consume ${reqComponent.name || 'component'} for ${spellName} (not in inventory — track manually)`,
            'spell-component-consumed',
            {
              spell: spellName,
              component: reqComponent.name || 'unknown',
              skipped: true,
              reason: 'not_in_inventory',
            }
          );
        }
      }
    }

    return result;
  }

  // Path 2: free-text description only ("a pearl worth 100 gp").
  if (details.description && String(details.description).trim().length > 0) {
    const matched = findInventoryMatchForDescription(details.description, inv);
    if (matched) {
      if (typeof setInventory === 'function') {
        setInventory(buildInventoryDecrementUpdater(matched.id));
      }
      result.consumed.push({
        itemId: matched.id,
        name: matched.name,
        value: Number(matched.value) || 0,
      });
      if (typeof logActivity === 'function') {
        logActivity(
          `Consumed ${matched.name} for ${spellName}`,
          'spell-component-consumed',
          {
            spell: spellName,
            component: matched.name,
            itemId: matched.id,
            value: Number(matched.value) || 0,
            consumed: true,
            matchedFromDescription: details.description,
          }
        );
      }
    } else {
      result.skipped.push({
        name: details.description,
        reason: 'not_in_inventory',
      });
      if (typeof logActivity === 'function') {
        logActivity(
          `Could not consume "${details.description}" for ${spellName} (not in inventory — track manually)`,
          'spell-component-consumed',
          {
            spell: spellName,
            component: details.description,
            skipped: true,
            reason: 'not_in_inventory',
          }
        );
      }
    }
    return result;
  }

  // Path 3: consumed flag set but no specificComponents AND no description.
  // Nothing to match against — log a skip so the player knows the form's
  // toggle didn't actually fire.
  result.skipped.push({
    name: spellName,
    reason: 'no_component_specified',
  });
  if (typeof logActivity === 'function') {
    logActivity(
      `${spellName}: consumed flag set but no component specified (track manually)`,
      'spell-component-consumed',
      {
        spell: spellName,
        skipped: true,
        reason: 'no_component_specified',
      }
    );
  }
  return result;
}

export default consumeMaterialComponents;
